import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RefreshFrequency = () => {
  const [schedules, setSchedules] = useState([
    {
      id: 1,
      system: 'SAP ERP',
      frequency: 'hourly',
      interval: 2,
      startTime: '00:00',
      endTime: '23:59',
      enabled: true,
      lastRun: '2026-02-10T02:30:00',
      nextRun: '2026-02-10T04:30:00',
    },
    {
      id: 2,
      system: 'Salesforce CRM',
      frequency: 'hourly',
      interval: 2,
      startTime: '00:00',
      endTime: '23:59',
      enabled: true,
      lastRun: '2026-02-10T02:28:00',
      nextRun: '2026-02-10T04:28:00',
    },
    {
      id: 3,
      system: 'Workday HCM',
      frequency: 'hourly',
      interval: 3,
      startTime: '06:00',
      endTime: '22:00',
      enabled: true,
      lastRun: '2026-02-10T02:25:00',
      nextRun: '2026-02-10T05:25:00',
    },
    {
      id: 4,
      system: 'Jira Project Management',
      frequency: 'daily',
      interval: 1,
      startTime: '02:00',
      endTime: '02:00',
      enabled: true,
      lastRun: '2026-02-10T02:00:00',
      nextRun: '2026-02-11T02:00:00',
    },
    {
      id: 5,
      system: 'Power BI Analytics',
      frequency: 'hourly',
      interval: 1,
      startTime: '00:00',
      endTime: '23:59',
      enabled: true,
      lastRun: '2026-02-10T02:32:00',
      nextRun: '2026-02-10T03:32:00',
    },
    {
      id: 6,
      system: 'Azure Data Lake',
      frequency: 'daily',
      interval: 1,
      startTime: '03:00',
      endTime: '03:00',
      enabled: false,
      lastRun: '2026-02-09T03:00:00',
      nextRun: null,
    },
  ]);

  const [editingId, setEditingId] = useState(null);

  const frequencyOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Not scheduled';
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleSchedule = (id) => {
    setSchedules(
      schedules?.map((schedule) =>
        schedule?.id === id
          ? { ...schedule, enabled: !schedule?.enabled }
          : schedule
      )
    );
  };

  const getFrequencyLabel = (schedule) => {
    if (schedule?.frequency === 'hourly') {
      return `Every ${schedule?.interval} hour${schedule?.interval > 1 ? 's' : ''}`;
    }
    if (schedule?.frequency === 'daily') {
      return `Daily at ${schedule?.startTime}`;
    }
    if (schedule?.frequency === 'weekly') {
      return `Weekly on ${schedule?.startTime}`;
    }
    return `Monthly on ${schedule?.startTime}`;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Automated Refresh Schedules
          </h2>
          <p className="text-sm text-muted-foreground font-caption mt-1">
            Configure data synchronization frequency for integrated systems
          </p>
        </div>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Add Schedule
        </Button>
      </div>
      <div className="space-y-3 md:space-y-4">
        {schedules?.map((schedule) => (
          <div
            key={schedule?.id}
            className="bg-card border border-border rounded-md p-4 md:p-6"
          >
            {editingId === schedule?.id ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                    {schedule?.system}
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Check"
                      onClick={() => setEditingId(null)}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Frequency"
                    options={frequencyOptions}
                    value={schedule?.frequency}
                    onChange={() => {}}
                  />
                  <Input
                    label="Interval"
                    type="number"
                    value={schedule?.interval}
                    min={1}
                  />
                  <Input
                    label="Start Time"
                    type="time"
                    value={schedule?.startTime}
                  />
                  <Input
                    label="End Time"
                    type="time"
                    value={schedule?.endTime}
                  />
                </div>

                <Checkbox
                  label="Enable automatic refresh"
                  checked={schedule?.enabled}
                  onChange={() => {}}
                />
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                      {schedule?.system}
                    </h3>
                    <div
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption ${
                        schedule?.enabled
                          ? 'text-success bg-success/10' :'text-muted-foreground bg-muted'
                      }`}
                    >
                      <Icon
                        name={schedule?.enabled ? 'CheckCircle2' : 'Circle'}
                        size={12}
                      />
                      <span>{schedule?.enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 md:mb-4">
                    {getFrequencyLabel(schedule)}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Active Window
                      </p>
                      <p className="text-sm font-medium text-foreground font-data">
                        {schedule?.startTime} - {schedule?.endTime}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Last Run
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {formatTimestamp(schedule?.lastRun)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Next Run
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {formatTimestamp(schedule?.nextRun)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex lg:flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => setEditingId(schedule?.id)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant={schedule?.enabled ? 'ghost' : 'default'}
                    size="sm"
                    iconName={schedule?.enabled ? 'Pause' : 'Play'}
                    onClick={() => toggleSchedule(schedule?.id)}
                  >
                    {schedule?.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="ghost" size="sm" iconName="RefreshCw">
                    Run Now
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RefreshFrequency;